import React from "react";
// eslint-disable-next-line
import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Chip,
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  CloseOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import Loader from "../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableTable from "../../../../../components/ReusableComponent/ReusableTable";
import { openSnackbar } from "../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";
import ExpandingPurchasedResumeDetails from "./ExpandingPurchasedResumeDetails";
import AddEditPurchasedResume from "./AddEditPurchasedResume";
import FiltersModal from "./FiltersModal";

import { PurchasedResume } from "../../../../../types/orders-managment/resume";

// ==============================|| PURCHASED RESUMES LIST ||============================== //

const PurchasedResumes = () => {
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<PurchasedResume | null>(null);
  const [selected, setSelected] = useState<any>("");
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );

  const handleClose = () => {
    setOpen(!open);
  };

  const handleFiltersModalClose = () => {
    setOpenFilters(false);
  };

  const handleRowExpand = (rowId: string | number) => {
    setExpandedRowId(expandedRowId === rowId ? null : rowId);
  };

  const columns = useMemo<ColumnDef<PurchasedResume>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.id}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="User ID" />,
        accessorKey: "user_id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.user_id}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Resume ID" />,
        accessorKey: "resume_id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.resume_id}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Resume Subscription ID" />,
        accessorKey: "resume_subscription_id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.resume_subscription_id}
          </Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Chip
            label={row.original.status ? "Active" : "Inactive"}
            color={row.original.status ? "success" : "error"}
            variant="filled"
            size="small"
          />
        ),
      },
      {
        header: () => <FormattedMessage id="Created At" />,
        accessorKey: "created_at",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.created_at}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: { className: "cell-center" },
        cell: ({ row }) => {
          const isExpanded = expandedRowId === row.original.id;
          const collapseIcon = isExpanded ? (
            <CloseOutlined style={{ color: theme.palette.error.main }} />
          ) : (
            <EyeOutlined />
          );

          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title={isExpanded ? "Collapse" : "View Details"}>
                <IconButton
                  color="secondary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleRowExpand(row.original.id!);
                  }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setSelectedCustomer(row.original);
                    setCustomerModal(true);
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PurchasedResume[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchedtext, setSearchedtext] = useState("");
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/purchased_resumes/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Purchased resume deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete purchased resume",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const id = urlParams.get("id");
      const user_id = urlParams.get("user_id");
      const resume_id = urlParams.get("resume_id");
      const resume_subscription_id = urlParams.get("resume_subscription_id");
      const status = urlParams.get("status");

      const response = await USERAXIOS.get(
        `/purchased_resumes?page=${currentPage}${
          searchQuery ? `&id=${searchQuery}` : ""
        }` +
          (id ? `&id=${id}` : "") +
          (user_id ? `&user_id=${user_id}` : "") +
          (resume_id ? `&resume_id=${resume_id}` : "") +
          (resume_subscription_id
            ? `&resume_subscription_id=${resume_subscription_id}`
            : "") +
          (status ? `&status=${status}` : "")
      );
      const { data, meta } = response.data;
      setResult(data);
      setCountPage(meta);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching purchased resumes",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [location.search]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleSearch = () => {
    setloadingSearsh(true);
    setCurrenttPage(1);
    setSearchQuery(searchedtext);
    setloadingSearsh(false);
  };

  const handleApplyFilters = (filters: any) => {
    setLoading(true);
    const { id, user_id, resume_id, resume_subscription_id, status } = filters;
    const urlParams = new URLSearchParams(window.location.search);

    // Clear existing filter params
    urlParams.delete("id");
    urlParams.delete("user_id");
    urlParams.delete("resume_id");
    urlParams.delete("resume_subscription_id");
    urlParams.delete("status");

    // Set new filter params
    if (id) urlParams.set("id", id);
    if (user_id) urlParams.set("user_id", user_id);
    if (resume_id) urlParams.set("resume_id", resume_id);
    if (resume_subscription_id)
      urlParams.set("resume_subscription_id", resume_subscription_id);
    if (status) urlParams.set("status", status);

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);

    setCurrenttPage(1);
    fetchData();
  };

  const handleClearFilters = () => {
    setLoading(true);
    window.history.pushState({}, "", `${window.location.pathname}`);
    setCurrenttPage(1);
    fetchData();
  };

  const hasActiveFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(
      urlParams.get("id") ||
      urlParams.get("user_id") ||
      urlParams.get("resume_id") ||
      urlParams.get("resume_subscription_id") ||
      urlParams.get("status")
    );
  };

  const enhancedData = useMemo(() => {
    return result.map((item) => ({
      ...item,
      getCanExpand: () => true,
      getIsExpanded: () => expandedRowId === item.id,
      getToggleExpandedHandler: () => () => handleRowExpand(item.id!),
    }));
  }, [result, expandedRowId]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              {hasActiveFilters() && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClearFilters}
                  sx={{ padding: ".3rem 2rem" }}
                >
                  <FormattedMessage id="Clear Filters" />
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<FilterOutlined />}
                onClick={() => setOpenFilters(true)}
                sx={{
                  padding: ".3rem 2rem",
                  gap: ".5rem",
                }}
              >
                <FormattedMessage id="Filters" />
              </Button>
            </Box>
          </Box>

          <ReusableTable
            data={enhancedData}
            columns={columns}
            countPage={countPage}
            handlePageChange={handlePageChange}
            handleSearchChange={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrenttPage={setCurrenttPage}
            loadingSearsh={loadingSearsh}
            searchedtext={searchedtext}
            setSearchedtext={setSearchedtext}
            modalToggler={() => {
              setCustomerModal(true);
              setSelectedCustomer(null);
            }}
            renderDetailPanel={({ row }: any) => (
              <ExpandingPurchasedResumeDetails data={row.original} />
            )}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
          />
        </>
      )}

      {openFilters && (
        <FiltersModal
          open={openFilters}
          handleClose={handleFiltersModalClose}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />
      )}

      <ReusableDeleteDialog
        title={selected?.id}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      {customerModal && (
        <AddEditPurchasedResume
          open={customerModal}
          setResult={setResult}
          fetchData={fetchData}
          id={selected}
          purchasedResume={selectedCustomer}
          handleClose={() => setCustomerModal(false)}
        />
      )}
    </>
  );
};

export default PurchasedResumes;
